import React, { useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import AdminMainContainer from 'components/admin/search/AdminMainContainer';
import {
  ExpandablePanel,
  ExpandablePanelHeaderName,
  FieldPanelFullWidth,
  H1NewUI,
  HeaderCaptionSemiBold,
} from 'styles/v2/Styles.styled';
import { PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import styled from 'styled-components';
import CreateToolButton from './CreateToolButton';
import AdminSearchToolsQueryHelper from './AdminSearchToolsQueryHelper';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from 'services/api/constants';
import { postSearchTools } from 'services/api/adminToolsApi';
import { Form, Formik } from 'formik';
import ToggleButton from 'components/v2/compiler/panels/toggleable/ToggleButton';
import WizardTextInputWithAutoComplete from 'components/v2/epd-wizard-inputs/WizardTextInputWithAutoComplete';
import SelectDataField from 'components/v2/form/select-data-field/SelectDataField';
import { ToolType } from 'types/tools/ToolType';
import { AdminLookupKeyEnum, Option } from 'types/types';
import getToolTypeLabel from 'util/getToolTypeLabel';
import { ToolHeadState } from 'types/tools/ToolHeadState';
import getToolHeadStateLabel from 'util/getToolHeadStateLabel';
import AdminSearchToolsResultsTable from './AdminSearchToolsResultsTable';
import AdminSearchToolsRemoveFilterChips from './AdminSearchToolsRemoveFilterChips';
import { YES_NO } from 'components/v2/compiler/constants';
import { useAdminLookup } from 'services/api/queries';

const TOOL_TYPE_OPTIONS: Option[] = Object.values(ToolType).map((x) => ({ value: x, label: getToolTypeLabel(x) }));
const HEAD_STATE_OPTIONS: Option[] = Object.values(ToolHeadState).map((x) => ({
  value: x,
  label: getToolHeadStateLabel(x),
}));
const HAS_API_INTEGRATION_OPTIONS: Option[] = [
  {
    value: YES_NO.YES,
    label: 'With API',
  },
  {
    value: YES_NO.NO,
    label: 'Without API',
  },
];
const NUMBER_OF_EPDS_OPTIONS: Option[] = [
  {
    value: '0-10',
    label: '0-10',
  },
  {
    value: '11-100',
    label: '11-100',
  },
  {
    value: '101-500',
    label: '101-500',
  },
  {
    value: '501',
    label: '501+',
  },
];

const AdminSearchTools: React.FC<RouteComponentProps> = ({ location, history }) => {
  const currentFilter = AdminSearchToolsQueryHelper.parse(location.search);
  const [toolAutoCompleteList, setToolAutoCompleteList] = useState<string[]>([]);
  const searchResultsQuery = useQuery({
    queryKey: [QUERY_KEYS.ADMIN_TOOL, location.search],
    queryFn: ({ signal }) => postSearchTools(currentFilter, { signal }),
    placeholderData: (prev) => prev, // This keeps previous data to avoid major layout shifts when changing page
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
  const pcrOptions = useAdminLookup(AdminLookupKeyEnum.PCRS).data;
  const cpcrOptions = useAdminLookup(AdminLookupKeyEnum.CPCRS).data;
  const toolOwnerOptions = useAdminLookup(AdminLookupKeyEnum.TOOL_OWNERS).data;

  return (
    <AdminMainContainer>
      <Heading>
        <H1NewUI>Tools</H1NewUI>
        <CreateToolButton />
      </Heading>

      <Formik
        initialValues={currentFilter}
        onSubmit={(values) => {
          const newSearchParams = AdminSearchToolsQueryHelper.stringify({
            ...values,
            pageNumber: 1,
          });
          history.push(location.pathname + newSearchParams);
        }}
        enableReinitialize
      >
        {({ values, setFieldValue, dirty, submitForm }) => (
          <ExpandablePanel
            style={{ marginTop: 38 }}
            headerTemplate={(options) => {
              return (
                <div className={`${options.className} ${options.collapsed ? 'header-collapsed' : ''}`}>
                  <ExpandablePanelHeaderName>
                    <HeaderCaptionSemiBold>Filters</HeaderCaptionSemiBold>
                    <ToggleButton options={options} />
                  </ExpandablePanelHeaderName>

                  {options.collapsed && (
                    <ChipsList>
                      <AdminSearchToolsRemoveFilterChips
                        filter={currentFilter}
                        pcrOptions={pcrOptions}
                        cpcrOptions={cpcrOptions}
                        numberOfEpdsOptions={NUMBER_OF_EPDS_OPTIONS}
                        toolOwnerOptions={toolOwnerOptions}
                      />
                    </ChipsList>
                  )}
                </div>
              );
            }}
            toggleable
          >
            <Form>
              <TwoColumnsLayout>
                <WizardTextInputWithAutoComplete
                  name="name"
                  label="Tool name"
                  value={values.name}
                  triggerOnChangedFor="keypress"
                  onChanged={setFieldValue}
                  onSelectSuggested={(value) => {
                    setFieldValue('name', value);
                    submitForm();
                  }}
                  onSubmit={() => {
                    submitForm();
                  }}
                  placeholder="Type here to search"
                  completeMethod={async ({ query }) => {
                    const result = await postSearchTools({
                      ...values,
                      name: query,
                      pageNumber: 1,
                      pageSize: 5,
                    });
                    setToolAutoCompleteList(result.items.map((x) => x.name));
                  }}
                  suggestions={toolAutoCompleteList}
                  maxLength={150}
                />

                <SelectDataField name="toolType" label="Tool Type" options={TOOL_TYPE_OPTIONS} isClearable />

                <SelectDataField name="pcrId" label="PCR" options={pcrOptions ?? []} isClearable />

                <SelectDataField name="cpcrId" label="C-PCR" options={cpcrOptions ?? []} isClearable />

                <SelectDataField
                  name="hasApiIntegration"
                  label="Integration type"
                  options={HAS_API_INTEGRATION_OPTIONS}
                  isClearable
                />

                <SelectDataField
                  name="numberOfPublishedEpds"
                  label="Number of EPDs published through the tool"
                  options={NUMBER_OF_EPDS_OPTIONS}
                  isClearable
                />

                <SelectDataField name="headState" label="Status" options={HEAD_STATE_OPTIONS} isClearable />

                <SelectDataField name="toolOwnerId" label="Tool Owner" options={toolOwnerOptions ?? []} isClearable />
              </TwoColumnsLayout>

              <ButtonsRow>
                <SecondaryButton
                  type="reset"
                  label="Clear"
                  onClick={() => history.push(location.pathname)}
                  disabled={!(dirty || AdminSearchToolsQueryHelper.isFilterActive(currentFilter))}
                />
                <PrimaryButton
                  type="submit"
                  label="Search"
                  loading={!!searchResultsQuery.data && searchResultsQuery.isFetching}
                  disabled={!dirty}
                />
              </ButtonsRow>
            </Form>
          </ExpandablePanel>
        )}
      </Formik>

      {(() => {
        if (searchResultsQuery.error) {
          const message = searchResultsQuery.error instanceof Error ? searchResultsQuery.error.message : 'Unknown error';
          return <FieldPanelFullWidth>Failed to load results: {message}</FieldPanelFullWidth>;
        }

        const searchResult = searchResultsQuery.data;
        if (!searchResult) {
          return null;
        }

        if (searchResult.totalCount === 0) {
          return <FieldPanelFullWidth>No tools found matching your search criteria</FieldPanelFullWidth>;
        }

        return (
          <FieldPanelFullWidth>
            <AdminSearchToolsResultsTable data={searchResult} showLoading={searchResultsQuery.isFetching} />
          </FieldPanelFullWidth>
        );
      })()}
    </AdminMainContainer>
  );
};

export default withGreyBackground(AdminSearchTools);

const Heading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  && ${PrimaryButton} {
    min-width: auto;
  }
`;

const ChipsList = styled.div`
  flex-grow: 1;
  display: flex;
  padding-left: 30px;
  flex-wrap: wrap;
  column-gap: 7px;
  row-gap: 4px;
`;

const TwoColumnsLayout = styled.div`
  display: grid;
  column-gap: 30px;
  row-gap: 29px;
  grid-template-columns: 1fr;

  ${(props) => props.theme.media.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ButtonsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 41px;
  gap: 16px;

  && ${PrimaryButton}, && ${SecondaryButton} {
    min-width: 140px;
  }
`;
